// Anzeige-Helfer des Technischen Monitorings (SPEC 4.5). Rein, ohne Netzwerk.
import type { MonitoringBot, MonitoringStatus } from "./api/types";
import { formatCount, formatDateTime } from "./format";

export type RobotsState = "allowed" | "partial" | "blocked";

/** Status als Text, die Farbe ist nur zusätzlich (CLAUDE.md „Frontend“). */
export const ROBOTS_LABEL: Record<RobotsState, string> = {
  allowed: "erlaubt",
  partial: "teilweise",
  blocked: "blockiert",
};
export const ROBOTS_CLASS: Record<RobotsState, string> = {
  allowed: "bg-success-bg text-success",
  partial: "bg-warning-bg text-warning",
  blocked: "bg-danger-bg text-danger",
};

export const KIND_LABEL: Record<MonitoringBot["kind"], string> = {
  training: "Training",
  search: "Suchindex",
  user: "Nutzer-Abruf",
  other: "Sonstiges",
};

/** Farbklasse eines UA-Test-Ergebnisses („200“ gut, „n. a.“ neutral, Blockaden rot, Rest orange). */
export function accessClass(label: string): string {
  if (label === "200") return "bg-success-bg text-success";
  if (label === "n. a.") return "bg-info-bg text-info";
  if (label === "Challenge" || label.endsWith("WAF") || label === "403") return "bg-danger-bg text-danger";
  return "bg-warning-bg text-warning";
}

/** „2 von 3 URLs“ nur, wenn nicht alle Abrufe gut waren. */
export function accessDetail(access: NonNullable<MonitoringBot["access"]>): string {
  if (access.tested === 0) return "";
  const parts: string[] = [];
  if (access.ok < access.tested) parts.push(`${access.ok} von ${access.tested} URLs`);
  if (access.waf) parts.push(access.waf);
  return parts.join(" · ");
}

export const DIFF_PREFIX: Record<string, string> = { "+": "+ ", "-": "− ", " ": "  ", "…": "⋯ " };

export type Kpi = { label: string; value: string; detail: string; tone: "good" | "warn" | "bad" | "muted" };

/** Die vier Kacheln des Mockups. Die zwei Logfile-Kacheln folgen mit S18. */
export function kpis(status: MonitoringStatus): Kpi[] {
  const last = status.last_check;
  const version = status.version;
  let robots: Kpi;
  if (last?.robots_error) {
    robots = { label: "robots.txt", value: "nicht abrufbar", detail: last.robots_error, tone: "bad" };
  } else if (!version) {
    robots = { label: "robots.txt", value: "noch nicht geprüft", detail: "Mit „Jetzt prüfen“ abrufen", tone: "muted" };
  } else if (last?.robots_changed) {
    robots = {
      label: "robots.txt",
      value: "geändert",
      detail: `${formatDateTime(version.first_seen_at)} · +${version.added} −${version.removed} Zeilen`,
      tone: "warn",
    };
  } else {
    robots = {
      label: "robots.txt",
      value: "unverändert",
      detail: `seit ${formatDateTime(version.first_seen_at)} · ${formatCount(status.version_count)} ${
        status.version_count === 1 ? "Version" : "Versionen"
      }`,
      tone: "good",
    };
  }
  const k = status.kpis;
  const bots: Kpi =
    k.allowed === null
      ? { label: "AI-Bots erlaubt", value: "—", detail: "noch keine Auswertung", tone: "muted" }
      : {
          label: "AI-Bots erlaubt",
          value: `${k.allowed} / ${k.total}`,
          detail: k.newly_blocked.length
            ? `${k.newly_blocked.join(", ")} neu blockiert`
            : k.blocked.length
              ? `blockiert: ${k.blocked.join(", ")}`
              : "keiner blockiert",
          tone: k.newly_blocked.length ? "bad" : "good",
        };
  const later = (label: string): Kpi => ({ label, value: "—", detail: "folgt mit S18 (Logfiles)", tone: "muted" });
  return [robots, bots, later("Verifizierte AI-Bot-Hits 7 T"), later("Gespoofte Bot-Hits 7 T")];
}

/** „letzter Check 08.10.2026, 06:00 · nächster Lauf 09.10.2026, 07:00“. */
export function headerLine(status: MonitoringStatus): string {
  const done = status.last_done_check;
  const last = done ? `letzter Check ${formatDateTime(done.finished_at ?? done.created_at)}` : "noch kein Check";
  return `${last} · nächster Lauf ${formatDateTime(status.next_run)}`;
}

/** Dauer des UA-Tests in Sekunden (ein Request je `gap` Sekunden). */
export function accessSeconds(requests: number, gap: number): number {
  return Math.ceil(requests * gap);
}

export const ALERT_RULES: { text: string; channel: string }[] = [
  { text: "robots.txt hat sich geändert", channel: "Slack #seo-alerts · sofort" },
  { text: "Ein Such- oder Nutzer-Abruf-Bot wird blockiert (robots.txt oder WAF)", channel: "Slack + E-Mail · sofort" },
  { text: "Verifizierte Hits eines Bots fallen um mehr als 50 % ggü. Vorwoche", channel: "E-Mail · täglich" },
  { text: "Mehr als 5 % 5xx-Antworten an AI-Bots", channel: "Slack · stündlich" },
  { text: "Sitemap nicht erreichbar oder leer", channel: "E-Mail · täglich" },
];
